import React from 'react';
import {
  Accordion,
  Badge,
  Banner,
  Button,
  ButtonText,
  Switch
} from '@morressier/alembic';
import { Box, Divider, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faClockFour,
  faFile,
  faMinus,
  faPlus
} from '@fortawesome/pro-regular-svg-icons';
import {
  ICoReview,
  IPeerReviewFirstAssessment,
  IPeerReviewRevisionAssessment,
  IReviewer,
  IReviewTimeline
} from '@publishing-engine/domain/models';
import AuthorHero from 'src/components/AuthorHero';
import { RATING_OPTIONS } from 'src/utils/reviews';
import EditorReview from '../../EditorReview';
import { useQuery } from '@tanstack/react-query';
import { fetchOrganizationAndJournalsInformation } from '../../../../../api/queries';
import { useSubmissionData } from '../../../../../contexts/SubmissionDataProvider';
import ReviewTimeline from '../../ReviewTimeline';
import NavbarDashboard from 'src/components/NavbarDashboard';
import FileViewerButtonText from 'src/components/Submission/FileViewerButtonText';
import OptInsReivew from './OptInsReview';
import { DisplayField } from 'src/components/shared/DisplayField';

interface Props {
  roundIndex: number;
  reviewer: IReviewer;
  coReview?: ICoReview | null;
  reviewFieldConfig: any;
  reviewTimeline?: IReviewTimeline;
  reviewAssessment: IPeerReviewFirstAssessment | IPeerReviewRevisionAssessment;
  /** Whether the review can still be rated — the caller owns the workflow gate. */
  canRateReview?: boolean;
  onRateReview?: () => void;
  onClose: () => void;
}

const ReviewDetailsReadonly: React.FC<Props> = ({
  roundIndex,
  reviewer,
  coReview,
  reviewAssessment,
  reviewFieldConfig,
  reviewTimeline,
  canRateReview = false,
  onRateReview,
  onClose
}) => {
  const { isOpen, onToggle } = useDisclosure();

  const { organizationId, submissionContextId } = useSubmissionData();
  const isCoReviewed = coReview?.status === 'accepted';
  const coReviewer =
    coReview?.coReviewer?.firstName && coReview?.coReviewer?.lastName
      ? `${coReview.coReviewer.firstName} ${coReview.coReviewer.lastName}`
      : coReview?.coReviewer.email;
  const reviewedBy = `By ${reviewer.firstName} ${reviewer.lastName} ${
    isCoReviewed ? `& ${coReviewer}` : ''
  }`;

  const { data: organizationData } = useQuery({
    queryKey: ['organization'],
    queryFn: () =>
      organizationId
        ? fetchOrganizationAndJournalsInformation(organizationId)
        : null
  });

  const reviewTimelineIcon = isOpen ? (
    <FontAwesomeIcon icon={faMinus} />
  ) : (
    <FontAwesomeIcon icon={faPlus} />
  );

  // TODO: Move this function component out of this component
  const getAccordionContentForAuthors = () => {
    return (
      <Flex
        gap={8}
        background="whiteSecondaryFill"
        flexDirection="column"
        marginY={4}
      >
        {reviewAssessment.feedbackForAuthors?.moderated && (
          <Banner
            variant="attention"
            heading="For your attention"
            content="In case of rejection or revision, Authors will only see the Moderated Feedback, which can still be adjusted as needed."
          />
        )}

        {reviewAssessment.feedbackForAuthors?.original && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Feedback for Authors:</Text>
            <Text textStyle="textBody">
              {reviewAssessment.feedbackForAuthors.original}
            </Text>
          </Flex>
        )}

        {reviewAssessment.feedbackForAuthors?.moderated && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Moderated Feedback for Authors</Text>
            <Text textStyle="textBody">
              {reviewAssessment.feedbackForAuthors.moderated}
            </Text>
          </Flex>
        )}

        {typeof reviewAssessment?.missingReference?.exists === 'boolean' && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingLine">Relevance of Cited Work</Text>
            <Text textStyle="textBody">
              {reviewAssessment?.missingReference?.exists
                ? 'No: The author did not reference recent and appropriate work.'
                : 'Yes: The author referenced recent and appropriate work.'}
            </Text>
          </Flex>
        )}

        {reviewAssessment?.missingReference?.exists && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingLine">Recommended References</Text>
            <Text textStyle="textBody">
              {reviewAssessment.missingReference.value}
            </Text>
          </Flex>
        )}

        {reviewAssessment.feedbackForAuthors?.visibility && (
          <Flex justifyContent="space-between" alignItems="center">
            <Box>
              <Text textStyle="headingItem">
                Exclude Feedback from Response to Author
              </Text>
              <Text textStyle="textBody">
                If Invited to Revise, Authors will not have access to this
                feedback.
              </Text>
            </Box>
            <Switch
              isChecked={
                reviewAssessment.feedbackForAuthors?.visibility === 'show'
              }
              isDisabled={true}
            />
          </Flex>
        )}

        {(reviewAssessment.reviewerAttachments || []).length > 0 ? (
          <Flex flexDirection="column" gap={4}>
            <Text textStyle="headingItem">Review Attachments</Text>
            <Banner
              variant="attention"
              heading="For your attention"
              content="Decide on the files that the Author should have access in case of rejection or revision."
            />

            <Flex flexDirection="column" gap={4} marginTop={4}>
              {reviewAssessment.reviewerAttachments?.map(
                (attachment, index) => (
                  <React.Fragment key={attachment.id}>
                    <Flex justifyContent="space-between">
                      <Flex gap={2} alignItems="center" flex={1} minWidth={0}>
                        <FontAwesomeIcon icon={faFile} />
                        <Text
                          textStyle="textBody"
                          wordBreak="break-word"
                          whiteSpace="normal"
                          flex={1}
                          minWidth={0}
                        >
                          {attachment.name}
                        </Text>
                        <FileViewerButtonText
                          file={attachment}
                          submissionContextId={submissionContextId}
                          textStyle="textBody"
                          flexShrink={0}
                        >
                          View
                        </FileViewerButtonText>
                      </Flex>
                      <Switch
                        isChecked={attachment?.visibility === 'show'}
                        isDisabled={true}
                      />
                    </Flex>
                    {index <
                    (reviewAssessment.reviewerAttachments?.length || 0) - 1 ? (
                      <Divider />
                    ) : null}
                  </React.Fragment>
                )
              )}
            </Flex>
          </Flex>
        ) : null}
      </Flex>
    );
  };

  // TODO: Move this function component out of this component
  const getAccordionContentForAboutReview = () => {
    const displayScore =
      RATING_OPTIONS[reviewAssessment?.reviewScore?.score || ''];

    const renderReviewScore = (displayScore?: {
      label: string;
      value: number;
    }) => {
      if (!reviewAssessment?.reviewScore?.score) {
        return (
          <Text textStyle="textBody">
            You have not yet rated the review provided by {reviewer.firstName}{' '}
            {reviewer.lastName}.
          </Text>
        );
      }

      return (
        <>
          {!!displayScore && (
            <Text textStyle="textBody">
              Review scored as {displayScore.value} ({displayScore.label}).
            </Text>
          )}
          {!!reviewAssessment.reviewScore?.justification && (
            <>
              <Text textStyle="headingItem">Review Feedback</Text>
              <Text textStyle="textBody" wordBreak="break-word">
                "{reviewAssessment.reviewScore?.justification}"
              </Text>
            </>
          )}
        </>
      );
    };

    return (
      <Flex
        gap={8}
        background="whiteSecondaryFill"
        marginTop={4}
        flexDirection="column"
      >
        <Flex alignItems="flex-start" justifyContent="space-between">
          <Flex gap={2} width="100%">
            <Flex
              flex="1"
              flexDirection="column"
              gap={2}
              alignItems="flex-start"
            >
              <AuthorHero reviewer={reviewer} showAvatar={false} />
              {!!displayScore && (
                <Badge variant="success" marginRight={4}>
                  R-score : {displayScore.value}
                </Badge>
              )}
            </Flex>
            {isCoReviewed && coReview.coReviewer && (
              <Flex
                flex="1"
                flexDirection="column"
                gap={2}
                alignItems="flex-start"
              >
                <AuthorHero
                  reviewer={{
                    ...coReview.coReviewer,
                    firstName: coReview.coReviewer.firstName || '',
                    lastName: coReview.coReviewer.lastName || '',
                    affiliationDetails: {
                      organizationName: coReview.coReviewer?.affiliation || '',
                      ringgoldId: coReview.coReviewer?.ringgoldId || ''
                    }
                  }}
                  showAvatar={false}
                />
                {!!displayScore && (
                  <Badge variant="success" marginRight={4}>
                    R-score : {displayScore.value}
                  </Badge>
                )}
              </Flex>
            )}
          </Flex>
        </Flex>
        <Flex>
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Submission Review Score</Text>
            {renderReviewScore(displayScore)}
            <Flex>
              <Badge>
                <Text textStyle="headingLine">Rate This Review</Text>
              </Badge>
            </Flex>
          </Flex>
        </Flex>

        {'receiveFeedback' in reviewAssessment && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Review Feedback Request</Text>
            <Text textStyle="textBody">
              {reviewAssessment.receiveFeedback === 'yes'
                ? 'This reviewer opted in to receive an evaluation of this report.'
                : 'This reviewer did not opt to receive an evaluation of this report.'}
            </Text>
          </Flex>
        )}

        {'areaOfExpertise' in reviewAssessment && (
          <Flex flexDirection="column" gap={2}>
            <Text textStyle="headingItem">Area of Expertise</Text>
            <Text textStyle="textBody">
              {reviewAssessment.areaOfExpertise === 'fullyMatched'
                ? "This submission is fully in the reviewer's area of expertise."
                : "This submission is partially in the reviewer's area of expertise."}
            </Text>
          </Flex>
        )}

        {'webOfScienceOptIn' in reviewAssessment &&
          reviewAssessment.webOfScienceOptIn !== undefined && (
            <DisplayField
              fieldName="webOfScienceOptIn"
              value={reviewAssessment.webOfScienceOptIn}
              config={reviewFieldConfig}
            />
          )}
      </Flex>
    );
  };

  return (
    <Overlay>
      <NavbarDashboard submissionContextId={submissionContextId} />
      <Flex flexDirection="column" backgroundColor="surfaceSecondaryFill">
        <Layout width="48rem" paddingY={4}>
          <Flex gap={5} flexDirection="column">
            <ButtonText
              leftIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              maxWidth="fit-content"
              onClick={onClose}
            >
              Back to Reviews
            </ButtonText>
            <Flex flexDirection="column">
              <Flex
                gap={4}
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <Flex gap={2} flexDirection="column">
                  <Flex gap={1} alignItems="center">
                    <FontAwesomeIcon icon={faClockFour} />
                    <Text textStyle="textBody">From Round {roundIndex}</Text>
                  </Flex>
                  <Text textStyle="headingSection">Assess this Review</Text>
                  <Flex gap={2} alignItems="center">
                    <Text>{reviewedBy}</Text>
                    {reviewTimeline && (
                      <ButtonText maxWidth="fit-content" onClick={onToggle}>
                        {reviewTimelineIcon} Review Timeline
                      </ButtonText>
                    )}
                  </Flex>
                </Flex>
                {canRateReview && (
                  <Button
                    variant="secondary"
                    flexShrink={0}
                    onClick={onRateReview}
                  >
                    Rate Review
                  </Button>
                )}
              </Flex>
              <Flex mt={2} w="100%">
                {reviewTimeline && (
                  <ReviewTimeline
                    isOpen={isOpen}
                    reviewTimelineData={reviewTimeline!}
                  />
                )}
              </Flex>
            </Flex>
            {isCoReviewed && (
              <Banner
                variant="attention"
                heading="Co-Review Notice"
                content="The following report was co-reviewed: both Reviewers will be notified of any changes or scores, and this Review will be credited to both accounts."
              />
            )}
            <Accordion
              data-testId="revisions-history-overview"
              chakraAccordionProps={{
                allowMultiple: true,
                defaultIndex: [],
                style: {
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
              accordionItemProps={{
                background: 'neutralPrimaryContentInverse'
              }}
              items={[
                {
                  title: 'For Editors',
                  subtitle:
                    'Review the scores, feedback and recommendations sent to you by the reviewer.',
                  content: organizationData && (
                    <EditorReview
                      reviewAssessment={reviewAssessment}
                      reviewFieldConfig={reviewFieldConfig}
                      organizationData={organizationData}
                      submissionId={submissionContextId}
                    />
                  )
                },

                {
                  title: 'For Authors',
                  subtitle:
                    'Review and moderate the feedback left by reviewers for authors.',
                  content: getAccordionContentForAuthors()
                },
                {
                  title: 'Opt-Ins',
                  content: (
                    <OptInsReivew
                      review={reviewAssessment}
                      reviewFieldConfig={reviewFieldConfig}
                    />
                  )
                },
                {
                  title: 'About the Review',
                  subtitle:
                    "Rate this Review, and verify the reviewer's profile and preferences.",
                  content: getAccordionContentForAboutReview()
                }
              ]}
            />
          </Flex>
        </Layout>
      </Flex>
    </Overlay>
  );
};

export default ReviewDetailsReadonly;
